import type { AxisRegion } from "./whiteboardAxes";

export type VisibleBoardRegionInput = {
  containerRect: { left: number; top: number };
  clientLeft: number;
  clientTop: number;
  clientWidth: number;
  clientHeight: number;
  scrollLeft: number;
  scrollTop: number;
  viewportWidth: number;
  viewportHeight: number;
  mode: "full" | "half";
  margin?: number;
};

/**
 * Fit a newly inserted graph to the portion of the Whiteboard scrollport that
 * is actually visible in the browser viewport. clientWidth/clientHeight exclude
 * the element's scrollbars; the rect intersection also excludes portions hidden
 * below the browser viewport when the page has been pushed down by its toolbars.
 */
export function getVisibleBoardRegion(input: VisibleBoardRegionInput): AxisRegion | null {
  const margin = Math.max(0, input.margin ?? 8);
  const contentLeft = input.containerRect.left + input.clientLeft;
  const contentTop = input.containerRect.top + input.clientTop;
  const visibleLeft = Math.max(contentLeft, 0) + margin;
  const visibleTop = Math.max(contentTop, 0) + margin;
  const visibleRight = Math.min(contentLeft + input.clientWidth, input.viewportWidth) - margin;
  const visibleBottom = Math.min(contentTop + input.clientHeight, input.viewportHeight) - margin;
  const visibleWidth = visibleRight - visibleLeft;
  const visibleHeight = visibleBottom - visibleTop;
  if (visibleWidth < 40 || visibleHeight < 40) return null;

  const width = input.mode === "half" ? Math.floor(visibleWidth / 2) : visibleWidth;
  return {
    left: input.scrollLeft + (visibleRight - contentLeft - width),
    top: input.scrollTop + (visibleTop - contentTop),
    width,
    height: visibleHeight,
  };
}
